import React, { useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"

import { Loader } from "shared/ui/Loader/Loader.component"
import { notify } from "shared/ui/Notifications/notify"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"

import {
  useProjectDetailsQuery,
  useProjectTypeQuery,
  ProjectNotFoundError,
} from "features/Projects/api/useProjectsQuery"
import { useUserStore } from "stores/userStore"
import { useProjectStore } from "stores/projectStore"
import { TabSpecContainer } from "features/TabSpec"
import { PROJECT_TYPE } from "types/project.types"

/**
 * Tables route component - Opens one tab spec of a project.
 *
 * Fetches the project via `useProjectDetailsQuery` from the ID in the URL,
 * syncs the result into the project store, and redirects home when the user
 * state is lost or the project does not exist. The project type (the cached
 * query the project page reads) says whether the project has a questionnaire:
 * a data-only or dashboard project has none, and its tab spec is built from
 * the data alone. The spec opens once the type is known: a type that cannot
 * be read (the request failed, or the server answered with an error, which
 * no notification reports) shows why, with a retry, rather than a guess.
 *
 * @component
 * @returns The Tables component.
 */
const Tables: React.FC = () => {
  const project = useProjectStore((state) => state.project)
  const setProject = useProjectStore((state) => state.setProject)
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)

  // Extracts the project ID and tab spec name from the URL parameters
  // useParams already decodes the segments.
  const { projectId, name } = useParams<{ projectId: string; name: string }>()

  const userReady = Boolean(user && user.username && user.unit)
  const detailsQuery = useProjectDetailsQuery(projectId, userReady)
  const typeQuery = useProjectTypeQuery(projectId ?? "", userReady)
  const type = (typeQuery.data ?? "").toLowerCase()
  const dataOnly = type === PROJECT_TYPE.DATA_ONLY || type === PROJECT_TYPE.DASHBOARD

  useEffect(() => {
    if (!userReady) {
      navigate("/home")
    } else if (!projectId) {
      navigate("/home")
      notify.error("Invalid Project", "No project ID provided in the URL.")
    }
  }, [userReady, projectId, navigate])

  // Sync fetched details into the project store used across the app.
  useEffect(() => {
    if (detailsQuery.data) {
      setProject({
        id: detailsQuery.data.id,
        Owner: detailsQuery.data.owner,
        ...detailsQuery.data.metadata,
      })
    }
  }, [detailsQuery.data, setProject])

  // Any load failure returns home; transport errors have already been
  // notified via the query's errorTitle meta.
  useEffect(() => {
    if (!detailsQuery.error) return
    navigate("/home")
    if (detailsQuery.error instanceof ProjectNotFoundError) {
      notify.error(
        "Project Not Found",
        "The project you are looking for does not exist."
      )
    }
  }, [detailsQuery.error, navigate])

  // Hold the loader until the store has the fetched project: consumers
  // capture the store on first mount, and the setProject sync effect runs
  // one tick after data arrives. A background refetch (say on reconnect)
  // keeps the tab spec mounted: unmounting it would drop unsaved edits and
  // open a second engine session. The type is waited for as well: the spec's
  // store is made once, with or without a questionnaire. A type read once
  // stays known when a later read fails.
  const isStoreSynced =
    detailsQuery.data !== undefined && project.id === detailsQuery.data.id
  const typeKnown = typeQuery.data !== undefined

  if (isStoreSynced && !typeKnown && typeQuery.isError) {
    return (
      <div className="h-full p-3 flex flex-col items-center gap-3 pt-24">
        <EmptyState
          message="The project type could not be read."
          subtitle="The tab spec needs it to know whether the project has a questionnaire."
        />
        <DataQButton
          type="secondary"
          isLoading={typeQuery.isFetching}
          onClick={() => void typeQuery.refetch()}
        >
          Try again
        </DataQButton>
      </div>
    )
  }

  if (!isStoreSynced || !typeKnown) {
    return (
      <div className="absolute left-2/4 top-2/4">
        <Loader fontSize={48} />
      </div>
    )
  }

  return (
    <>
      <title>{`${project.Name} · ${name}`}</title>
      <TabSpecContainer key={`${project.id}/${name}`} name={name ?? ""} dataOnly={dataOnly} />
    </>
  )
}

export default Tables
